package org.moxultimate.topwithmox.libraries

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Text
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.base.rememberViewModel

/**
 * Demonstrates Kotlin Flow + MVVM architecture in Kobweb.
 *
 * Flows work natively in Kotlin/JS — StateFlow, SharedFlow, and collectAsState()
 * are all fully supported. The only missing piece from Android is the lifecycle-
 * aware ViewModel; that gap is filled by the custom [ViewModel] base class in
 * `core/ViewModel.kt`.
 *
 * Pattern summary:
 * - [MutableStateFlow]  → holds UI state; composables observe it with [collectAsState].
 * - [MutableSharedFlow] → fires one-shot events (navigation, toasts, errors) that
 *                         the UI collects in a [LaunchedEffect].
 * - [ViewModel.viewModelScope] → coroutine scope that lives exactly as long as the
 *                         composable that owns the ViewModel.
 * - [rememberViewModel] → creates the ViewModel once and disposes it on removal.
 */

// ── 1. UI State — a plain data class snapshot of everything the screen needs ──

/**
 * Immutable snapshot of the counter screen's UI state.
 * Every field has a sensible default so partial updates are trivial via [copy].
 */
data class CounterUiState(
    val count: Int = 0,
    val isLoading: Boolean = false,
    val history: List<String> = emptyList(),
    val error: String? = null,
)

// ── 2. One-shot events — things that happen once, not persisted in state ──────

/**
 * Side-effect events emitted by [CounterViewModel] via [SharedFlow].
 * Use these for actions that should not be part of persistent state:
 * navigation triggers, toasts, dialogs, etc.
 */
sealed interface CounterEvent {
    /** Fired when the counter hits the defined limit. */
    data object LimitReached : CounterEvent

    /** Fired when the counter is reset. */
    data class Notification(val message: String) : CounterEvent
}

// ── 3. ViewModel — business logic + state management ─────────────────────────

/**
 * ViewModel for the counter demo screen.
 *
 * Exposes:
 * - [uiState] — [StateFlow] that the UI observes continuously.
 * - [events]  — [SharedFlow] for one-shot side effects.
 *
 * All mutations go through the ViewModel; the UI only calls functions and
 * reads state — it never mutates state directly (MVVM unidirectional data flow).
 */
class CounterViewModel : ViewModel() {

    companion object {
        private const val MAX_COUNT = 10
    }

    private val _uiState = MutableStateFlow(CounterUiState())
    /** Read-only state exposed to the UI. */
    val uiState: StateFlow<CounterUiState> = _uiState.asStateFlow()

    private val _events = MutableSharedFlow<CounterEvent>()
    /** One-shot events; collect in a [LaunchedEffect] in the composable. */
    val events: SharedFlow<CounterEvent> = _events.asSharedFlow()

    /** Simulates an async increment (e.g., a network call). */
    fun increment() {
        viewModelScope.launch {
            if (_uiState.value.count >= MAX_COUNT) {
                _events.emit(CounterEvent.LimitReached)
                return@launch
            }
            _uiState.update { it.copy(isLoading = true) }
            kotlinx.coroutines.delay(300)   // simulate async work
            _uiState.update { state ->
                val newCount = state.count + 1
                state.copy(
                    count = newCount,
                    isLoading = false,
                    history = state.history + "+1 → $newCount",
                )
            }
        }
    }

    /** Decrements, bottomed at zero. */
    fun decrement() {
        viewModelScope.launch {
            _uiState.update { state ->
                val newCount = maxOf(0, state.count - 1)
                state.copy(
                    count = newCount,
                    history = state.history + "-1 → $newCount",
                )
            }
        }
    }

    /** Resets state and fires a [CounterEvent.Notification]. */
    fun reset() {
        viewModelScope.launch {
            _uiState.update { CounterUiState() }
            _events.emit(CounterEvent.Notification("Counter reset to 0"))
        }
    }
}

// ── 4. Screen composable — observes state, handles events ─────────────────────

/**
 * Counter screen that demonstrates the full MVVM + Flow pattern.
 *
 * Key Compose + Flow integration points:
 * - `viewModel.uiState.collectAsState()` — re-renders whenever state changes.
 * - `LaunchedEffect(viewModel)` — collects one-shot [CounterEvent] exactly once
 *   per ViewModel instance; cancelled when the composable leaves.
 */
@Composable
fun FlowSample() {
    // ViewModel is created once; clear() is called automatically on removal
    val viewModel = rememberViewModel { CounterViewModel() }

    // Continuously observe state — triggers recomposition on every emission
    val uiState by viewModel.uiState.collectAsState()

    // Local state for displaying the last received event notification
    var lastEvent by remember { mutableStateOf("None") }

    // Collect one-shot events — LaunchedEffect is cancelled & re-started if
    // viewModel reference changes (in practice it never does with rememberViewModel)
    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            lastEvent = when (event) {
                is CounterEvent.LimitReached      -> "⚠ Limit of 10 reached!"
                is CounterEvent.Notification      -> event.message
            }
        }
    }

    Column(Modifier.gap(1.cssRem)) {

        // ── State display ─────────────────────────────────────────────────────
        SpanText("Count: ${uiState.count}  |  Loading: ${uiState.isLoading}")
        SpanText("Last event: $lastEvent")

        // ── Actions ───────────────────────────────────────────────────────────
        Row(Modifier.gap(0.5.cssRem)) {
            Button(
                onClick = { viewModel.increment() },
                enabled = !uiState.isLoading,
            ) { Text(if (uiState.isLoading) "…" else "+1") }

            Button(onClick = { viewModel.decrement() }) { Text("-1") }
            Button(onClick = { viewModel.reset() })     { Text("Reset") }
        }

        // ── History — derived directly from state, no extra local state needed ─
        if (uiState.history.isNotEmpty()) {
            SpanText("History:")
            Column(Modifier.gap(0.25.cssRem)) {
                uiState.history.takeLast(5).forEach { SpanText("  $it") }
            }
        }
    }
}
